// src/components/ui/file-upload.tsx
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Trash, Video } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import {
  useDropzone,
  type DropzoneState,
  type FileRejection,
} from 'react-dropzone'
import { BlueCheckIcon, JpgIcon, PdfIcon, UploadIcon } from '../Icons'

interface FileUploadProps {
  onFileSelect?: (file: File | null) => void
  onMultipleFileSelect?: (files: File[]) => void
  acceptedFileTypes?: string[]
  maxFileSize?: number
  className?: string
  previewImage?: string
  multiple?: boolean
  maxFiles?: number
  existingFiles?: File[]
  onRemoveFile?: (index: number) => void
  getDropZone?: (
    dropzoneState: DropzoneState,
    uploadedFile: File | null
  ) => React.ReactNode
  onError?: (error: string) => void
  hint?: string
  previewImageHeadText?: string
  previewImageSubText?: string
  isVideo?: boolean
  isPdf?: boolean
}

export function FileUpload({
  onFileSelect,
  onMultipleFileSelect,
  acceptedFileTypes = ['image/jpeg', 'image/jpg', 'image/png'],
  maxFileSize = 5 * 1024 * 1024, // 5MB
  className,
  previewImage,
  multiple = false,
  maxFiles = 10,
  existingFiles = [],
  onRemoveFile,
  getDropZone,
  onError,
  hint,
  previewImageHeadText,
  previewImageSubText,
  isVideo = false,
  isPdf = false,
}: Readonly<FileUploadProps>) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isRemoved, setIsRemoved] = useState(false)

  useEffect(() => {
    if (previewImage && !uploadedFile && !isRemoved) {
      setPreviewUrl(previewImage)
    }
  }, [previewImage, uploadedFile, isRemoved])

  const validateFiles = useCallback(
    (files: File[]) => {
      const errors: string[] = []

      files.forEach((file, index) => {
        // Check file type
        if (!acceptedFileTypes.includes(file.type)) {
          const allowedExtensions = acceptedFileTypes
            .map(type => type.split('/')[1].toUpperCase())
            .join(', ')
          errors.push(
            `File ${index + 1} "${file.name}": Only ${allowedExtensions} files are allowed`
          )
        }

        // Check file size
        if (file.size > maxFileSize) {
          const sizeMB = Math.round(maxFileSize / (1024 * 1024))
          errors.push(
            `File ${index + 1} "${file.name}": File size must be less than ${sizeMB}MB`
          )
        }
      })

      return errors
    },
    [acceptedFileTypes, maxFileSize]
  )

  const showError = useCallback(
    (error: string) => {
      setErrorMessage(error)
      onError?.(error)

      setTimeout(() => {
        setErrorMessage('')
      }, 5000)
    },
    [onError]
  )

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setErrorMessage('')

      if (rejectedFiles.length > 0) {
        const errors: string[] = []
        rejectedFiles.forEach(rejection => {
          const file = rejection.file
          if (
            rejection.errors.some(
              (err: { code: string }) => err.code === 'file-invalid-type'
            )
          ) {
            const allowedExtensions = acceptedFileTypes
              .map(type => type.split('/')[1].toUpperCase())
              .join(', ')
            errors.push(
              `"${file.name}": Only ${allowedExtensions} files are allowed`
            )
          }
          if (
            rejection.errors.some(
              (err: { code: string }) => err.code === 'file-too-large'
            )
          ) {
            const sizeMB = Math.round(maxFileSize / (1024 * 1024))
            const sizeKB = Math.round(maxFileSize / 1024)
            const sizeDisplay =
              maxFileSize >= 1024 * 1024 ? `${sizeMB}MB` : `${sizeKB}KB`
            errors.push(
              `"${file.name}": File size must be less than ${sizeDisplay}`
            )
          }
        })

        if (errors.length > 0) {
          showError(errors.join('; '))
          return
        }
      }

      const validationErrors = validateFiles(acceptedFiles)
      if (validationErrors.length > 0) {
        showError(validationErrors.join('; '))
        return
      }

      if (multiple) {
        const remainingSlots = maxFiles - existingFiles.length

        if (acceptedFiles.length > remainingSlots) {
          if (remainingSlots === 0) {
            showError(
              `Maximum ${maxFiles} files allowed. Please remove some files first.`
            )
          } else {
            showError(
              `Only ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'} can be added. Maximum ${maxFiles} files allowed.`
            )
          }
          return
        }

        const filesToAdd = acceptedFiles.slice(0, remainingSlots)

        if (filesToAdd.length > 0) {
          onMultipleFileSelect?.(filesToAdd)
        }
      } else {
        const file = acceptedFiles[0]
        if (file) {
          setIsRemoved(false)
          setIsUploading(true)
          setUploadProgress(0)
          setPreviewUrl(URL.createObjectURL(file))

          // Simulate upload progress
          const interval = setInterval(() => {
            setUploadProgress(prev => {
              if (prev >= 100) {
                clearInterval(interval)
                setIsUploading(false)
                setUploadedFile(file)
                onFileSelect?.(file)
                return 100
              }
              return prev + 10
            })
          }, 100)
        }
      }
    },
    [
      onFileSelect,
      onMultipleFileSelect,
      multiple,
      maxFiles,
      existingFiles,
      validateFiles,
      showError,
      acceptedFileTypes,
      maxFileSize,
    ]
  )

  const dropZoneState = useDropzone({
    onDrop,
    accept: acceptedFileTypes.reduce(
      (acc, type) => {
        acc[type] = []
        return acc
      },
      {} as Record<string, string[]>
    ),
    maxSize: maxFileSize,
    multiple: multiple,
    noClick: false,
    noKeyboard: false,
  })

  const removeFile = () => {
    setIsRemoved(true)
    setUploadedFile(null)
    setUploadProgress(0)
    setErrorMessage('')
    setPreviewUrl(null)
    onFileSelect?.(null)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    )
  }

  // For multiple mode, show upload area if there's space for more files
  const showUploadArea = multiple
    ? existingFiles.length < maxFiles
    : !previewUrl && !uploadedFile && !isUploading

  const getFileInfoText = () => {
    if (uploadedFile) {
      return formatFileSize(uploadedFile.size)
    }

    if (previewImage && !isUploading) {
      if (previewImageSubText) {
        return previewImageSubText
      } else {
        if (isPdf) {
          return 'Existing PDF Document'
        } else {
          return 'Existing profile picture'
        }
      }
    }

    return 'Processing...'
  }

  const getHintText = () => {
    if (hint) {
      return hint
    }

    if (isPdf) {
      return 'PDF (max 5MB)'
    }

    if (multiple) {
      return `PNG, JPG (max. 800x400px each) - ${existingFiles.length}/${maxFiles} files`
    }

    return 'PNG, JPG (max. 800x400px)'
  }

  if (getDropZone) {
    return getDropZone(dropZoneState, uploadedFile)
  }

  return (
    <div className={cn('space-y-3', className)}>
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          <strong>Upload Error:</strong> {errorMessage}
        </div>
      )}

      {/* Upload area */}
      {showUploadArea && (
        <div
          {...dropZoneState.getRootProps()}
          className={cn(
            'border-1 border-ss-neutral-100 rounded-lg p-6 text-center cursor-pointer transition-colors',
            'hover:border-ss-blue-500 hover:bg-ss-blue-50/50',
            dropZoneState.isDragActive && 'border-ss-blue-500 bg-ss-blue-50/50',
            errorMessage && 'border-red-300 bg-red-50/30'
          )}
        >
          <input
            {...dropZoneState.getInputProps()}
            aria-label="File upload"
            aria-describedby="file-upload-desc"
          />
          <div className="flex flex-col items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                'h-9 px-3 border-ss-neutral-200 text-ss-neutral-700 hover:bg-ss-action-gray-50',
                errorMessage && 'border-red-300 text-red-700'
              )}
              aria-label="Upload file"
            >
              <UploadIcon className="w-4 h-4" aria-hidden="true" />
            </Button>
            <div className="text-center">
              <p
                className={cn(
                  'text-sm font-medium text-ss-neutral-950',
                  errorMessage && 'text-red-700'
                )}
                id="file-upload-desc"
              >
                <span
                  className={cn(
                    'text-ss-blue-500 mr-2',
                    errorMessage && 'text-red-600'
                  )}
                >
                  Click to upload
                </span>
                <span>or drag and drop</span>
              </p>
              <p
                className={cn(
                  'text-xs text-ss-neutral-600 mt-1',
                  errorMessage && 'text-red-600'
                )}
              >
                {getHintText()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Display existing files in multiple mode */}
      {multiple && existingFiles.length > 0 && (
        <div className="space-y-3 max-h-96 overflow-auto">
          {existingFiles.map((file, idx) => (
            <div
              key={`${file.name}-${idx}`}
              className="border border-ss-neutral-100 rounded-lg p-4"
            >
              <div className="flex items-start gap-3">
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name || 'Preview'}
                  className="size-16 object-cover rounded-md border border-ss-neutral-100"
                  style={{ minWidth: 64, minHeight: 64 }}
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-ss-neutral-950 truncate">
                      {file.name}
                    </p>
                    <div className="flex items-center">
                      <BlueCheckIcon className="w-5 h-5 text-ss-blue-500 flex-shrink-0" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveFile?.(idx)}
                        className="h-6 w-6 p-0 text-ss-neutral-400 hover:text-ss-neutral-600"
                        aria-label="Remove file"
                      >
                        <Trash className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-ss-neutral-600" aria-live="polite">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(previewUrl || isUploading || uploadedFile) && (
        <div className="border border-ss-neutral-100 rounded-lg p-4">
          <div className="flex items-start gap-3">
            {!previewUrl && <JpgIcon className="size-8 text-ss-blue-500" />}

            {previewUrl && isVideo && (
              <div className="relative size-16 rounded-md border border-ss-neutral-100 bg-ss-neutral-50 flex items-center justify-center overflow-hidden">
                <video
                  src={previewUrl}
                  className="size-full object-cover"
                  muted
                  playsInline
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Video className="w-8 h-8 text-white" aria-hidden="true" />
                </div>
              </div>
            )}

            {previewUrl && isPdf && !isVideo && (
              <div className="relative size-16 rounded-md border border-ss-neutral-100 bg-ss-neutral-50 flex items-center justify-center overflow-hidden">
                <PdfIcon className="size-8 text-red-600" />
              </div>
            )}

            {previewUrl && !isVideo && !isPdf && (
              <img
                src={previewUrl}
                alt={uploadedFile?.name ?? 'Preview'}
                className="size-16 object-cover rounded-md border border-ss-neutral-100"
                style={{ minWidth: 64, minHeight: 64 }}
              />
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium text-ss-neutral-950 truncate">
                  {uploadedFile?.name ||
                    (previewImage && !isUploading
                      ? (previewImageHeadText ?? 'Current Profile Image')
                      : 'Uploading...')}
                </p>
                {(uploadedFile || (previewImage && !isUploading)) && (
                  <div className="flex items-center">
                    <BlueCheckIcon className="w-5 h-5 text-ss-blue-500 flex-shrink-0" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeFile}
                      className="h-6 w-6 p-0 text-ss-neutral-400 hover:text-ss-neutral-600"
                      aria-label="Remove file"
                    >
                      <Trash className="w-3 h-3" aria-hidden="true" />
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-ss-neutral-600" aria-live="polite">
                {getFileInfoText()}
              </p>
              {isUploading && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 bg-ss-neutral-100 rounded-full h-2">
                    <div
                      className="bg-ss-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-ss-neutral-700">
                    {uploadProgress}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
